import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import html2canvas from "html2canvas";
import { API_BASE } from "../utils/api";

/* Product type + Random/Fix legends */
const TYPE_LEGEND = [
  { code: "A", label: "Angle" },
  { code: "C", label: "Channel" },
  { code: "F", label: "Flat" },
  { code: "R", label: "Round" },
  { code: "S", label: "Square" },
];
const RF_LEGEND = [
  { code: "R", label: "Random" },
  { code: "F", label: "Fix" },
];

// Payment terms = all the cart's options, MINUS the UBS option.
const PAYMENT_TERMS = [
  "100% RTGS Against Proforma Invoice",
  "Next Day RTGS",
  "10 days RTGS Rate +300",
  "15 days RTGS rate +450",
];

const ROW_COUNT = 12;

const last10 = (v) => String(v ?? "").replace(/\D/g, "").slice(-10);

function loadFirms(dealerPhone) {
  try {
    const raw = localStorage.getItem(`firmsCache:v1:${dealerPhone}`);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return (parsed?.payload?.firms || [])
      .map((f) => ({
        firmName: String(f.firmName || "").trim(),
        gst: String(f.gst || "").trim().toUpperCase(),
        area: String(f.area || "").trim(),
      }))
      .filter((f) => f.firmName);
  } catch {
    return [];
  }
}

const emptyRow = () => ({
  type: { A: false, C: false, F: false, R: false, S: false },
  size: "",
  rf: { R: false, F: false },
  qty: "",
  remarks: "",
});

export default function OrderRequestForm({
  open,
  onClose,
  dealerPhone,
  onAddToCart,
}) {
  const formRef = useRef(null);
  const [firms, setFirms] = useState([]);
  const [selectedFirm, setSelectedFirm] = useState("");
  const [gst, setGst] = useState("");
  const [area, setArea] = useState("");
  const [rows, setRows] = useState(() =>
    Array.from({ length: ROW_COUNT }, emptyRow)
  );
  const [paymentTerm, setPaymentTerm] = useState("");
  const [vehicleNo, setVehicleNo] = useState("");
  const [formRemarks, setFormRemarks] = useState("");
  const [busy, setBusy] = useState("");
  const [toast, setToast] = useState("");

  useEffect(() => {
    if (!open) return;
    const f = loadFirms(dealerPhone);
    setFirms(f);
    // Auto-select if only one firm.
    if (f.length === 1) {
      setSelectedFirm(f[0].firmName);
      setGst(f[0].gst);
      setArea(f[0].area);
    }
    // Fresh blank form every time it opens — no leftover rows / vehicle /
    // payment term from a previous session.
    setRows(Array.from({ length: ROW_COUNT }, emptyRow));
    setPaymentTerm("");
    setVehicleNo("");
    setFormRemarks("");
  }, [open, dealerPhone]);

  const firmByName = useMemo(() => {
    const m = new Map();
    firms.forEach((f) => m.set(f.firmName, f));
    return m;
  }, [firms]);

  const onFirmChange = (name) => {
    setSelectedFirm(name);
    const f = firmByName.get(name);
    setGst(f?.gst || "");
    setArea(f?.area || "");
  };

  const toggleType = (i, code) =>
    setRows((prev) =>
      prev.map((r, idx) =>
        idx === i ? { ...r, type: { ...r.type, [code]: !r.type[code] } } : r
      )
    );
  const toggleRf = (i, code) =>
    setRows((prev) =>
      prev.map((r, idx) =>
        idx === i ? { ...r, rf: { ...r.rf, [code]: !r.rf[code] } } : r
      )
    );
  const setCell = (i, key, val) =>
    setRows((prev) =>
      prev.map((r, idx) => (idx === i ? { ...r, [key]: val } : r))
    );

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(""), 3500);
  };

  // Convert the filled form rows into scan-style items (no image needed)
  // so the cart pipeline can match products + add them.
  const buildScanItems = () => {
    const typeName = Object.fromEntries(TYPE_LEGEND.map((t) => [t.code, t.label]));
    const rfName = Object.fromEntries(RF_LEGEND.map((t) => [t.code, t.label]));
    return rows
      .map((r) => {
        const typeCode = TYPE_LEGEND.map((t) => t.code).find((c) => r.type[c]);
        const rfCode = RF_LEGEND.map((t) => t.code).find((c) => r.rf[c]);
        return {
          productType: typeCode ? typeName[typeCode] : "",
          size: String(r.size || "").trim(),
          qty: r.qty,
          randomFix: rfCode ? rfName[rfCode] : "",
          remarks: String(r.remarks || "").trim(),
          paymentTerm,
          vehicleNo,
          confidence: "high",
        };
      })
      .filter((it) => Number(it.qty) > 0 && (it.productType || it.size));
  };

  const handleAddToCart = () => {
    if (!selectedFirm) {
      showToast("Please select a Party (firm) name first.");
      return;
    }
    const items = buildScanItems();
    if (!items.length) {
      showToast("Fill at least one row — tick a Product Type / Size and enter Qty.");
      return;
    }
    onAddToCart?.(items, { paymentTerm, vehicleNo });
  };

  const fileName = () =>
    `Order-Request-${(selectedFirm || "form").replace(/\s+/g, "-")}-${last10(
      dealerPhone
    )}.png`;

  // Canvas capture of the on-screen form (keeps the exact on-screen design).
  // onclone converts every form control into static text/marks so
  // html2canvas captures the SELECTED firm, the TYPED values and the
  // ticked checkboxes (raw controls don't render reliably).
  const buildCanvas = async () => {
    const node = formRef.current;
    if (!node) return null;
    const canvas = await html2canvas(node, {
      scale: 2,
      backgroundColor: "#ffffff",
      useCORS: true,
      onclone: (doc) => {
        // <select> → its chosen value
        doc.querySelectorAll("select").forEach((el) => {
          const span = doc.createElement("span");
          span.textContent = el.value || "";
          span.style.cssText =
            "font-size:13px;font-weight:600;padding:2px 6px;display:inline-block;";
          if (el.parentNode) el.parentNode.replaceChild(span, el);
        });
        // checkboxes → a drawn box (✕ if ticked)
        doc.querySelectorAll('input[type="checkbox"]').forEach((el) => {
          const box = doc.createElement("span");
          box.style.cssText =
            "display:inline-block;width:14px;height:14px;border:1.5px solid #333;border-radius:2px;text-align:center;line-height:11px;font-size:12px;font-weight:bold;color:#111;";
          box.textContent = el.checked ? "✓" : "";
          if (el.parentNode) el.parentNode.replaceChild(box, el);
        });
        // remaining text inputs → their typed value
        doc.querySelectorAll("input").forEach((el) => {
          const span = doc.createElement("span");
          span.textContent = el.value || "";
          span.style.cssText =
            "display:inline-block;width:100%;font-size:11px;text-align:center;";
          if (el.parentNode) el.parentNode.replaceChild(span, el);
        });
      },
    });
    return canvas;
  };

  // Download the captured form as a PNG.
  const canvasToPngBlob = (canvas) =>
    new Promise((resolve) => {
      if (canvas.toBlob) canvas.toBlob((b) => resolve(b), "image/png");
      else {
        // Fallback for browsers without toBlob.
        const dataUrl = canvas.toDataURL("image/png");
        const bin = atob(dataUrl.split(",")[1]);
        const arr = new Uint8Array(bin.length);
        for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
        resolve(new Blob([arr], { type: "image/png" }));
      }
    });

  const handleDownload = async () => {
    if (busy) return;
    if (!selectedFirm) {
      showToast("Please select a Party (firm) name first.");
      return;
    }
    setBusy("download");
    try {
      // PNG of the on-screen form (exact design, ticked boxes, typed values).
      const canvas = await buildCanvas();
      if (!canvas) throw new Error("capture failed");
      const blob = await canvasToPngBlob(canvas);
      if (!blob) throw new Error("png build failed");

      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = fileName();
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      showToast("Download failed. Please try again.");
    } finally {
      setBusy("");
    }
  };

  // Build the small form-data payload (checked codes only) for the server
  // to render into a PDF — keeps the request body well under the 8KB WAF
  // limit, so it isn't blocked by CloudFront.
  const buildFormData = () => ({
    partyName: selectedFirm,
    gst,
    area,
    rows: rows.map((r) => ({
      type: Object.keys(r.type).filter((k) => r.type[k]),
      size: r.size,
      rf: Object.keys(r.rf).filter((k) => r.rf[k]),
      qty: r.qty,
      remarks: r.remarks,
    })),
    paymentTerm,
    vehicleNo,
    remarks: formRemarks,
  });

  const handleSendWhatsApp = async () => {
    if (busy) return;
    if (!selectedFirm) {
      showToast("Please select a Party (firm) name first.");
      return;
    }
    setBusy("whatsapp");
    try {
      const res = await fetch(`${API_BASE}/api/send-order-form`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          phone: last10(dealerPhone),
          data: buildFormData(), // server renders the PDF
          filename: fileName(),
          caption: `Order Request Form — ${selectedFirm}`,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.success) {
        showToast("✅ Form PDF sent to your WhatsApp!");
      } else {
        showToast(data.message || "WhatsApp send failed.");
      }
    } catch (e) {
      showToast("WhatsApp send failed. Please try again.");
    } finally {
      setBusy("");
    }
  };

  if (!open) return null;

  const cellInput =
    "w-full px-1 py-0.5 text-xs outline-none bg-transparent text-center";

  return createPortal(
    <div className="fixed inset-0 z-[400] bg-black/60 flex items-start justify-center p-2 sm:p-4 overflow-y-auto">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl my-4">
        {/* Toolbar (NOT captured) */}
        <div className="flex items-center justify-between gap-2 px-3 sm:px-4 py-2.5 sm:py-3 border-b sticky top-0 bg-white rounded-t-xl z-10">
          <h3 className="font-bold text-gray-900 text-sm sm:text-lg shrink-0">
            Order Form
          </h3>
          <div className="flex items-center gap-1.5 sm:gap-2">
            {onAddToCart && (
              <button
                onClick={handleAddToCart}
                disabled={!!busy}
                className="px-2.5 sm:px-3 py-1.5 text-xs sm:text-sm font-bold rounded-lg bg-yellow-500 text-black hover:bg-yellow-600 disabled:opacity-50 whitespace-nowrap"
              >
                🛒 <span className="hidden sm:inline">Add to Cart</span>
                <span className="sm:hidden">Cart</span>
              </button>
            )}
            <button
              onClick={handleDownload}
              disabled={!!busy}
              className="px-2.5 sm:px-3 py-1.5 text-xs sm:text-sm font-bold rounded-lg bg-gray-800 text-white hover:bg-black disabled:opacity-50 whitespace-nowrap"
            >
              {busy === "download" ? "..." : "⬇ Download"}
            </button>
            <button
              onClick={handleSendWhatsApp}
              disabled={!!busy}
              className="px-2.5 sm:px-3 py-1.5 text-xs sm:text-sm font-bold rounded-lg bg-green-600 text-white hover:bg-green-700 disabled:opacity-50 whitespace-nowrap"
            >
              {busy === "whatsapp" ? "..." : (
                <>
                  <span className="sm:hidden">WhatsApp</span>
                  <span className="hidden sm:inline">Send to WhatsApp</span>
                </>
              )}
            </button>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-900 text-2xl leading-none px-1 shrink-0"
              aria-label="Close"
            >
              ×
            </button>
          </div>
        </div>

        {/* ===== Captured printable form ===== */}
        <div className="p-3 sm:p-5">
          <div
            ref={formRef}
            className="bg-white text-black mx-auto w-full max-w-[720px] p-3 sm:p-5"
          >
            <h2 className="text-center font-bold text-base sm:text-lg mb-4">
              ORDER REQUEST FORM
            </h2>

            {/* Party / GST / Area */}
            <div className="space-y-2 text-xs sm:text-sm mb-3">
              <div className="flex items-center gap-2">
                <span className="font-bold w-24 sm:w-28 shrink-0">
                  Party Name:
                </span>
                {firms.length > 1 ? (
                  <select
                    value={selectedFirm}
                    onChange={(e) => onFirmChange(e.target.value)}
                    className="flex-1 min-w-0 border border-gray-400 rounded px-2 py-1 text-xs sm:text-sm"
                  >
                    <option value="">-- Select Party (Firm) --</option>
                    {firms.map((f) => (
                      <option key={f.firmName + f.gst} value={f.firmName}>
                        {f.firmName}
                      </option>
                    ))}
                  </select>
                ) : (
                  // Single firm — no dropdown, just show the name.
                  <span className="font-semibold">{selectedFirm || "—"}</span>
                )}
              </div>
              <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2">
                <div className="flex items-start gap-2">
                  <span className="font-bold w-24 sm:w-28 shrink-0">
                    GST Number:
                  </span>
                  <span className="font-mono break-all">{gst || "—"}</span>
                </div>
                <div className="flex items-start gap-2 sm:ml-4">
                  <span className="font-bold w-24 sm:w-auto shrink-0">Area:</span>
                  <span className="break-words">{area || "—"}</span>
                </div>
              </div>
            </div>

            {/* Legends */}
            <div className="flex flex-wrap justify-end gap-x-3 gap-y-0.5 text-[10px] font-semibold mb-1">
              {TYPE_LEGEND.map((t) => (
                <span key={t.code}>
                  {t.code} = {t.label}
                </span>
              ))}
            </div>

            {/* Table */}
            <div className="overflow-x-auto">
            <table className="w-full border-collapse border border-black text-xs min-w-[320px]">
              <thead>
                <tr className="bg-gray-100">
                  <th className="border border-black p-1 w-[24%]">
                    Product Type
                    <div className="font-normal text-[9px]">(A/C/F/R/S)</div>
                  </th>
                  <th className="border border-black p-1">Size</th>
                  <th className="border border-black p-1 w-[16%]">
                    Random/Fix
                    <div className="font-normal text-[9px]">(R/F)</div>
                  </th>
                  <th className="border border-black p-1">Qty (kgs)</th>
                  <th className="border border-black p-1">Remarks</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={i}>
                    <td className="border border-black p-0.5">
                      <div className="flex justify-center gap-1">
                        {TYPE_LEGEND.map((t) => (
                          <label
                            key={t.code}
                            className="flex flex-col items-center text-[9px] leading-none"
                          >
                            <input
                              type="checkbox"
                              checked={r.type[t.code]}
                              disabled={
                                Object.values(r.type).some(Boolean) &&
                                !r.type[t.code]
                              }
                              onChange={() => toggleType(i, t.code)}
                              className="w-4 h-4 sm:w-5 sm:h-5 accent-yellow-600 disabled:opacity-25 disabled:cursor-not-allowed"
                            />
                            <span>{t.code}</span>
                          </label>
                        ))}
                      </div>
                    </td>
                    <td className="border border-black p-0.5">
                      <input
                        value={r.size}
                        onChange={(e) => setCell(i, "size", e.target.value)}
                        className={cellInput}
                      />
                    </td>
                    <td className="border border-black p-0.5">
                      <div className="flex justify-center gap-2">
                        {RF_LEGEND.map((t) => (
                          <label
                            key={t.code}
                            className="flex flex-col items-center text-[9px] leading-none"
                          >
                            <input
                              type="checkbox"
                              checked={r.rf[t.code]}
                              disabled={
                                Object.values(r.rf).some(Boolean) &&
                                !r.rf[t.code]
                              }
                              onChange={() => toggleRf(i, t.code)}
                              className="w-4 h-4 sm:w-5 sm:h-5 accent-yellow-600 disabled:opacity-25 disabled:cursor-not-allowed"
                            />
                            <span>{t.code}</span>
                          </label>
                        ))}
                      </div>
                    </td>
                    <td className="border border-black p-0.5">
                      <input
                        value={r.qty}
                        onChange={(e) => setCell(i, "qty", e.target.value)}
                        className={cellInput}
                      />
                    </td>
                    <td className="border border-black p-0.5">
                      <input
                        value={r.remarks}
                        onChange={(e) => setCell(i, "remarks", e.target.value)}
                        className={cellInput}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>

            {/* R/F full forms */}
            <div className="text-[10px] font-semibold mt-1">
              {RF_LEGEND.map((t) => `${t.code} = ${t.label}`).join("    ")}
            </div>

            {/* Payment terms */}
            <div className="flex flex-wrap items-center gap-4 mt-4 text-sm">
              <span className="font-bold">Payment Terms:</span>
              {PAYMENT_TERMS.map((term) => (
                <label key={term} className="flex items-center gap-1.5">
                  <input
                    type="checkbox"
                    checked={paymentTerm === term}
                    onChange={() =>
                      setPaymentTerm(paymentTerm === term ? "" : term)
                    }
                    className="w-4 h-4"
                  />
                  <span>{term}</span>
                </label>
              ))}
              <span className="text-[10px] text-gray-500">(Select any one)</span>
            </div>

            {/* Vehicle + Remarks */}
            <div className="mt-4 space-y-3 text-sm">
              <div className="flex items-center gap-2">
                <span className="font-bold w-28 shrink-0">Vehicle No:</span>
                <input
                  value={vehicleNo}
                  onChange={(e) => setVehicleNo(e.target.value)}
                  className="flex-1 border-b border-black px-1 py-0.5 outline-none"
                />
              </div>
              <div className="flex items-center gap-2">
                <span className="font-bold w-28 shrink-0">Remarks:</span>
                <input
                  value={formRemarks}
                  onChange={(e) => setFormRemarks(e.target.value)}
                  className="flex-1 border-b border-black px-1 py-0.5 outline-none"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {toast && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 bg-gray-900 text-white px-4 py-2 rounded-lg text-sm font-semibold shadow-xl z-[500]">
          {toast}
        </div>
      )}
    </div>,
    document.body
  );
}
